/** @file tilePalette: the colours already used in the puppet's sheets, most frequent first. */
import type { Image, Rgba } from '@zilo/pose3d-image-io/types/image';
import { PLACEHOLDER_COLORS } from '../../preview/placeholderColors';

/** The RGBA key of a colour as `tilePalette` counts it. */
const keyOf = (c: Rgba): number => (((c[0] << 24) | (c[1] << 16) | (c[2] << 8) | c[3]) >>> 0);

/** The preview placeholder colours never belong to the puppet's art. */
const SKIPPED: ReadonlySet<number> = new Set(Object.values(PLACEHOLDER_COLORS).map(keyOf));

/**
 * Counts every visible pixel (alpha above 0) of `sheets` by exact RGBA and returns the `limit` most frequent colours
 * (ties keep the order they were first met in); the preview's placeholder colours are left out.
 */
export function tilePalette(sheets: readonly Image[], limit = 32): Rgba[] {
  const counts = new Map<number, number>();
  for (const sheet of sheets) {
    const view = new DataView(sheet.data.buffer, sheet.data.byteOffset, sheet.data.byteLength);
    for (let i = 0; i + 3 < sheet.data.length; i += 4) {
      if (sheet.data[i + 3] === 0) continue;
      const key = view.getUint32(i);
      if (SKIPPED.has(key)) continue;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([k]) => [(k >>> 24) & 255, (k >>> 16) & 255, (k >>> 8) & 255, k & 255]);
}
